import React, { useState, useEffect, useRef, Suspense, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, useGLTF, Html } from "@react-three/drei";
import * as THREE from "three";
import { useTwin } from "../context/TwinContext";
import { openModelDB, loadModel } from "./ModelUpload";
import "./DigitalTwinViewer.css";

// ── Model mapping ──────────────────────────────────────────────
const CDN = "https://d22wm4k6mf9kpv.cloudfront.net/models";
const MODEL_MAP: Record<string, string> = {
  'robot arm': `${CDN}/robot.glb`, robot: `${CDN}/robot.glb`,
  pump: `${CDN}/pump.glb`, mixer: `${CDN}/pump.glb`, blender: `${CDN}/pump.glb`,
  press: `${CDN}/pump.glb`, 'press machine': `${CDN}/pump.glb`,
  filler: `${CDN}/pump.glb`, 'filler machine': `${CDN}/pump.glb`,
  capper: `${CDN}/pump.glb`, labeler: `${CDN}/pump.glb`,
  compressor: `${CDN}/pump.glb`, 'case packer': `${CDN}/pump.glb`,
  conveyor: `${CDN}/turbine.glb`, 'conveyor belt': `${CDN}/turbine.glb`,
  turbine: `${CDN}/turbine.glb`, generator: `${CDN}/turbine.glb`,
  palletizer: `${CDN}/turbine.glb`,
  reactor: `${CDN}/reactor.glb`, 'batch reactor': `${CDN}/reactor.glb`,
  oven: `${CDN}/oven.glb`, 'heat exchanger': `${CDN}/oven.glb`,
  separator: `${CDN}/reactor.glb`, distillation: `${CDN}/reactor.glb`,
  transformer: `${CDN}/reactor.glb`, substation: `${CDN}/reactor.glb`,
  welding: `${CDN}/robot.glb`, 'welding gun': `${CDN}/robot.glb`,
  vision: `${CDN}/robot.glb`, 'vision system': `${CDN}/robot.glb`,
};

function getModelPath(type: string): string {
  const n = type.toLowerCase().trim();
  if (MODEL_MAP[n]) return MODEL_MAP[n];
  for (const [k, v] of Object.entries(MODEL_MAP)) {
    if (n.includes(k) || k.includes(n)) return v;
  }
  return `${CDN}/pump.glb`;
}

function getModelScale(p: string): number {
  if (p.includes('robot')) return 0.25;
  if (p.includes('reactor')) return 0.2;
  if (p.includes('turbine')) return 0.2;
  if (p.includes('oven')) return 0.08;
  if (p.includes('mixer')) return 0.15;
  return 0.15;
}

// Preload
const unique = [...new Set(Object.values(MODEL_MAP))];
if (typeof window !== 'undefined') unique.forEach(p => useGLTF.preload(p));

// ── Status helpers ─────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  running: '#10b981', idle: '#fbbf24', fault: '#ef4444',
};

// ── Factory Building Shell ──────────────────────────────────────
const FACTORY_W = 40, FACTORY_D = 30, FACTORY_H = 10;
const WALL_COLOR = '#1c1c2e';
const BEAM_COLOR = '#2a2a3e';
const PILLAR_COLOR = '#252540';

function FactoryFloor() {
  const hw = FACTORY_W / 2, hd = FACTORY_D / 2;
  // Hazard chevron stripe helper
  const HazardStripe = ({ x, z, w, d: depth, rot = 0 }: { x: number; z: number; w: number; d: number; rot?: number }) => (
    <group>
      {Array.from({ length: Math.floor(w / 0.6) }, (_, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, rot + (i % 2 === 0 ? 0.4 : -0.4), 0]}
          position={[x - w / 2 + i * 0.6 + 0.3, 0.025, z]}>
          <planeGeometry args={[0.25, depth]} />
          <meshBasicMaterial color="#d4a017" transparent opacity={0.7} />
        </mesh>
      ))}
    </group>
  );

  return (
    <group>
      {/* Base concrete */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
        <planeGeometry args={[FACTORY_W, FACTORY_D]} />
        <meshStandardMaterial color="#1a1a2c" roughness={0.95} metalness={0.05} />
      </mesh>
      {/* Subtle tile grid */}
      <gridHelper args={[FACTORY_W, 20, '#252540', '#1f1f35']} position={[0, 0.005, 0]} />

      {/* Walkway lanes — lighter concrete paths */}
      {/* Main aisle X */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.015, hd - 2]}>
        <planeGeometry args={[FACTORY_W - 2, 2.5]} />
        <meshStandardMaterial color="#252540" roughness={0.85} metalness={0.05} />
      </mesh>
      {/* Main aisle Z */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-hw + 2, 0.015, 0]}>
        <planeGeometry args={[2.5, FACTORY_D - 2]} />
        <meshStandardMaterial color="#252540" roughness={0.85} metalness={0.05} />
      </mesh>

      {/* Walkway edge lines — white dashed */}
      {[hd - 0.7, hd - 3.3].map((z, i) => (
        <mesh key={`wl-${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, z]}>
          <planeGeometry args={[FACTORY_W - 2, 0.08]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.25} />
        </mesh>
      ))}
      {[-hw + 0.7, -hw + 3.3].map((x, i) => (
        <mesh key={`wlz-${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.02, 0]}>
          <planeGeometry args={[0.08, FACTORY_D - 2]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.25} />
        </mesh>
      ))}

      {/* Hazard chevrons at entrance */}
      <HazardStripe x={0} z={hd - 0.3} w={8} d={0.5} />

      {/* Safety perimeter — yellow border lines */}
      {[-1, 1].map(side => (
        <mesh key={`bx-${side}`} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, side * (hd - 0.5)]}>
          <planeGeometry args={[FACTORY_W, 0.12]} />
          <meshBasicMaterial color="#d4a017" transparent opacity={0.5} />
        </mesh>
      ))}
      {[-1, 1].map(side => (
        <mesh key={`bz-${side}`} rotation={[-Math.PI / 2, 0, 0]} position={[side * (hw - 0.5), 0.02, 0]}>
          <planeGeometry args={[0.12, FACTORY_D]} />
          <meshBasicMaterial color="#d4a017" transparent opacity={0.5} />
        </mesh>
      ))}

      {/* Zone labels on floor */}
      {[
        { text: 'ZONE A — PRODUCTION', x: 5, z: -5 },
        { text: 'ZONE B — ASSEMBLY', x: 5, z: 5 },
        { text: '⚠ KEEP CLEAR', x: 0, z: hd - 2 },
      ].map((lbl, i) => (
        <Html key={i} position={[lbl.x, 0.03, lbl.z]} rotation={[-Math.PI / 2, 0, 0]}
          style={{ pointerEvents: 'none' }} center>
          <div style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 3,
            color: i === 2 ? '#d4a017' : 'rgba(255,255,255,0.12)',
            fontFamily: 'monospace', whiteSpace: 'nowrap', userSelect: 'none',
            textTransform: 'uppercase',
          }}>{lbl.text}</div>
        </Html>
      ))}

      {/* Machine zone pads — darker squares where machines sit */}
      {[-8, -2, 4, 10].map((x, i) => (
        <mesh key={`pad-${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.012, -2]}>
          <planeGeometry args={[4.5, 4.5]} />
          <meshStandardMaterial color="#161628" roughness={0.95} metalness={0.05} />
        </mesh>
      ))}
    </group>
  );
}

function FactoryWalls() {
  const wallMat = <meshStandardMaterial color={WALL_COLOR} roughness={0.85} metalness={0.15} transparent opacity={0.6} side={THREE.DoubleSide} />;
  const hw = FACTORY_W / 2, hd = FACTORY_D / 2, h = FACTORY_H;
  return (
    <group>
      {/* Back wall */}
      <mesh position={[0, h / 2, -hd]}>
        <planeGeometry args={[FACTORY_W, h]} />
        {wallMat}
      </mesh>
      {/* Left wall */}
      <mesh position={[-hw, h / 2, 0]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[FACTORY_D, h]} />
        {wallMat}
      </mesh>
      {/* Right wall */}
      <mesh position={[hw, h / 2, 0]} rotation={[0, -Math.PI / 2, 0]}>
        <planeGeometry args={[FACTORY_D, h]} />
        {wallMat}
      </mesh>
      {/* Front wall (partial — two panels with gap for "entrance") */}
      <mesh position={[-hw / 2 - 2, h / 2, hd]}  rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[hw - 4, h]} />
        {wallMat}
      </mesh>
      <mesh position={[hw / 2 + 2, h / 2, hd]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[hw - 4, h]} />
        {wallMat}
      </mesh>
    </group>
  );
}

function FactoryPillars() {
  const pillars: [number, number][] = [];
  const hw = FACTORY_W / 2 - 1, hd = FACTORY_D / 2 - 1;
  // Pillars along walls
  for (let x = -hw; x <= hw; x += FACTORY_W / 4) {
    pillars.push([x, -hd], [x, hd]);
  }
  for (let z = -hd; z <= hd; z += FACTORY_D / 3) {
    pillars.push([-hw, z], [hw, z]);
  }
  // Deduplicate
  const unique = pillars.filter((p, i, arr) =>
    arr.findIndex(q => Math.abs(q[0] - p[0]) < 0.5 && Math.abs(q[1] - p[1]) < 0.5) === i
  );

  return (
    <group>
      {unique.map(([x, z], i) => (
        <mesh key={i} position={[x, FACTORY_H / 2, z]} castShadow>
          <boxGeometry args={[0.4, FACTORY_H, 0.4]} />
          <meshStandardMaterial color={PILLAR_COLOR} roughness={0.7} metalness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

function CeilingBeams() {
  const beams: JSX.Element[] = [];
  // Cross beams along X
  for (let z = -FACTORY_D / 2 + 5; z <= FACTORY_D / 2 - 5; z += FACTORY_D / 4) {
    beams.push(
      <mesh key={`bx-${z}`} position={[0, FACTORY_H - 0.2, z]}>
        <boxGeometry args={[FACTORY_W, 0.3, 0.25]} />
        <meshStandardMaterial color={BEAM_COLOR} roughness={0.6} metalness={0.4} />
      </mesh>
    );
  }
  // Cross beams along Z
  for (let x = -FACTORY_W / 2 + 5; x <= FACTORY_W / 2 - 5; x += FACTORY_W / 5) {
    beams.push(
      <mesh key={`bz-${x}`} position={[x, FACTORY_H - 0.2, 0]}>
        <boxGeometry args={[0.25, 0.3, FACTORY_D]} />
        <meshStandardMaterial color={BEAM_COLOR} roughness={0.6} metalness={0.4} />
      </mesh>
    );
  }
  // Ceiling plane (semi-transparent)
  beams.push(
    <mesh key="ceiling" position={[0, FACTORY_H, 0]} rotation={[Math.PI / 2, 0, 0]}>
      <planeGeometry args={[FACTORY_W, FACTORY_D]} />
      <meshStandardMaterial color="#141425" transparent opacity={0.35} side={THREE.DoubleSide} />
    </mesh>
  );
  return <group>{beams}</group>;
}

function OverheadLights() {
  const lights: JSX.Element[] = [];
  const cols = 3, rows = 2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = (c - (cols - 1) / 2) * (FACTORY_W / (cols + 1));
      const z = (r - (rows - 1) / 2) * (FACTORY_D / (rows + 1));
      lights.push(
        <group key={`ol-${r}-${c}`} position={[x, FACTORY_H - 0.5, z]}>
          <mesh>
            <boxGeometry args={[2.5, 0.15, 0.5]} />
            <meshStandardMaterial color="#333350" metalness={0.6} roughness={0.4} />
          </mesh>
          <mesh position={[0, -0.12, 0]}>
            <boxGeometry args={[2.2, 0.05, 0.35]} />
            <meshBasicMaterial color="#e8e0ff" transparent opacity={0.7} />
          </mesh>
          <pointLight intensity={0.4} distance={18} color="#ddd8ff" position={[0, -0.5, 0]} />
        </group>
      );
    }
  }
  return <group>{lights}</group>;
}

// ── Overhead Crane ─────────────────────────────────────────────
function OverheadCrane() {
  const trolleyRef = useRef<THREE.Group>(null);
  const hookRef = useRef<THREE.Mesh>(null);
  const railY = FACTORY_H - 1.2;
  const hw = FACTORY_W / 2 - 2;

  useFrame(({ clock }) => {
    if (trolleyRef.current) {
      trolleyRef.current.position.x = Math.sin(clock.elapsedTime * 0.15) * (hw - 2);
    }
    if (hookRef.current) {
      hookRef.current.position.y = -1.5 + Math.sin(clock.elapsedTime * 0.3) * 0.4;
    }
  });

  return (
    <group>
      {/* Rails — two I-beams running along X */}
      {[-4, 4].map(z => (
        <group key={`rail-${z}`}>
          <mesh position={[0, railY, z]}>
            <boxGeometry args={[FACTORY_W - 4, 0.2, 0.3]} />
            <meshStandardMaterial color="#f59e0b" roughness={0.5} metalness={0.6} />
          </mesh>
          {/* Rail supports hanging from ceiling */}
          {Array.from({ length: 5 }, (_, i) => {
            const x = -hw + 2 + i * ((FACTORY_W - 8) / 4);
            return (
              <mesh key={i} position={[x, railY + 0.6, z]}>
                <boxGeometry args={[0.15, 1.2, 0.15]} />
                <meshStandardMaterial color={BEAM_COLOR} roughness={0.6} metalness={0.4} />
              </mesh>
            );
          })}
        </group>
      ))}

      {/* Bridge beam (connects the two rails, moves along X) */}
      <group ref={trolleyRef} position={[0, railY, 0]}>
        <mesh>
          <boxGeometry args={[0.35, 0.25, 8.6]} />
          <meshStandardMaterial color="#f59e0b" roughness={0.4} metalness={0.7} />
        </mesh>
        {/* Trolley box */}
        <mesh position={[0, -0.3, 0]}>
          <boxGeometry args={[0.8, 0.4, 1]} />
          <meshStandardMaterial color="#334155" roughness={0.5} metalness={0.6} />
        </mesh>
        {/* Cable */}
        <mesh position={[0, -1, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 1.2, 4]} />
          <meshStandardMaterial color="#94a3b8" metalness={0.8} roughness={0.2} />
        </mesh>
        {/* Hook */}
        <mesh ref={hookRef} position={[0, -1.5, 0]}>
          <torusGeometry args={[0.12, 0.03, 6, 12, Math.PI]} />
          <meshStandardMaterial color="#e2e8f0" metalness={0.9} roughness={0.1} />
        </mesh>
      </group>
    </group>
  );
}

// ── Catwalks ───────────────────────────────────────────────────
function Catwalks() {
  const catY = FACTORY_H * 0.55;
  const hw = FACTORY_W / 2;
  const hd = FACTORY_D / 2;
  const railH = 0.8;

  const Railing = ({ length, axis }: { length: number; axis: 'x' | 'z' }) => (
    <group>
      {/* Top rail */}
      <mesh position={[0, railH, 0]}>
        <boxGeometry args={axis === 'x' ? [length, 0.05, 0.05] : [0.05, 0.05, length]} />
        <meshStandardMaterial color="#f59e0b" roughness={0.5} metalness={0.6} />
      </mesh>
      {/* Mid rail */}
      <mesh position={[0, railH * 0.5, 0]}>
        <boxGeometry args={axis === 'x' ? [length, 0.04, 0.04] : [0.04, 0.04, length]} />
        <meshStandardMaterial color="#d97706" roughness={0.5} metalness={0.5} transparent opacity={0.7} />
      </mesh>
      {/* Posts */}
      {Array.from({ length: Math.floor(length / 3) + 1 }, (_, i) => {
        const offset = -length / 2 + i * (length / Math.floor(length / 3));
        return (
          <mesh key={i} position={axis === 'x' ? [offset, railH / 2, 0] : [0, railH / 2, offset]}>
            <boxGeometry args={[0.05, railH, 0.05]} />
            <meshStandardMaterial color="#f59e0b" roughness={0.5} metalness={0.6} />
          </mesh>
        );
      })}
    </group>
  );

  return (
    <group>
      {/* Back wall catwalk */}
      <group position={[0, catY, -hd + 1.2]}>
        {/* Platform */}
        <mesh>
          <boxGeometry args={[FACTORY_W - 4, 0.1, 1.8]} />
          <meshStandardMaterial color="#1e1e35" roughness={0.8} metalness={0.3} />
        </mesh>
        {/* Grating pattern */}
        <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[FACTORY_W - 4, 1.8]} />
          <meshBasicMaterial color="#2a2a45" transparent opacity={0.4} wireframe />
        </mesh>
        {/* Outer railing */}
        <group position={[0, 0, 0.95]}>
          <Railing length={FACTORY_W - 4} axis="x" />
        </group>
        {/* Support brackets */}
        {Array.from({ length: 5 }, (_, i) => {
          const x = -hw + 4 + i * ((FACTORY_W - 8) / 4);
          return (
            <group key={i}>
              <mesh position={[x, -catY / 2, 0]}>
                <boxGeometry args={[0.15, catY, 0.15]} />
                <meshStandardMaterial color={PILLAR_COLOR} roughness={0.7} metalness={0.3} />
              </mesh>
              {/* Diagonal brace */}
              <mesh position={[x, -catY / 4, 0.5]} rotation={[0.6, 0, 0]}>
                <boxGeometry args={[0.08, catY * 0.6, 0.08]} />
                <meshStandardMaterial color={PILLAR_COLOR} roughness={0.7} metalness={0.3} />
              </mesh>
            </group>
          );
        })}
      </group>

      {/* Right wall short catwalk */}
      <group position={[hw - 1.2, catY, 0]}>
        <mesh>
          <boxGeometry args={[1.5, 0.1, 14]} />
          <meshStandardMaterial color="#1e1e35" roughness={0.8} metalness={0.3} />
        </mesh>
        <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[1.5, 14]} />
          <meshBasicMaterial color="#2a2a45" transparent opacity={0.4} wireframe />
        </mesh>
        <group position={[-0.8, 0, 0]}>
          <Railing length={14} axis="z" />
        </group>
      </group>
    </group>
  );
}

// ── Ambient Factory Hum ────────────────────────────────────────
function useFactoryAmbience(enabled: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let ctx: AudioContext;
    try {
      ctx = new AudioContext();
    } catch { return; }
    ctxRef.current = ctx;

    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    gainRef.current = gain;

    // Low hum — 60Hz fundamental
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 60;
    osc1.connect(gain);
    osc1.start();

    // Harmonic — 120Hz
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 120;
    const g2 = ctx.createGain();
    g2.gain.value = 0.3;
    osc2.connect(g2).connect(gain);
    osc2.start();

    // Fade in
    gain.gain.linearRampToValueAtTime(0.04, ctx.currentTime + 2);

    return () => {
      osc1.stop(); osc2.stop();
      ctx.close();
    };
  }, [enabled]);

  return gainRef;
}

// ── Animated Machine Parts ─────────────────────────────────────
type MachineCategory = 'rotary' | 'pump' | 'arm' | 'reactor' | 'conveyor';

function getMachineCategory(type: string): MachineCategory {
  const t = type.toLowerCase();
  if (/robot|arm|welding|vision/.test(t)) return 'arm';
  if (/conveyor|belt|palletizer/.test(t)) return 'conveyor';
  if (/turbine|generator|compressor|fan/.test(t)) return 'rotary';
  if (/reactor|oven|heat|separator|distill|transform|substation/.test(t)) return 'reactor';
  return 'pump'; // pump, mixer, press, filler, capper, labeler, etc.
}

function SpinningFan({ speed }: { speed: number }) {
  const ref = useRef<THREE.Group>(null);
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * speed;
  });
  return (
    <group ref={ref} position={[0, 2.8, 0]}>
      {[0, 1, 2, 3].map(i => (
        <mesh key={i} rotation={[0, (i * Math.PI) / 2, Math.PI / 6]} position={[0, 0, 0]}>
          <boxGeometry args={[0.08, 1.2, 0.3]} />
          <meshStandardMaterial color="#60a5fa" metalness={0.8} roughness={0.2} />
        </mesh>
      ))}
      {/* Hub */}
      <mesh>
        <cylinderGeometry args={[0.15, 0.15, 0.3, 12]} />
        <meshStandardMaterial color="#94a3b8" metalness={0.9} roughness={0.1} />
      </mesh>
    </group>
  );
}

function PistonPump({ speed }: { speed: number }) {
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    if (ref.current) {
      ref.current.position.y = 2.2 + Math.sin(clock.elapsedTime * speed) * 0.4;
    }
  });
  return (
    <group position={[0, 0, 0]}>
      {/* Cylinder housing */}
      <mesh position={[0, 2.2, 0]}>
        <cylinderGeometry args={[0.25, 0.25, 1.2, 12]} />
        <meshStandardMaterial color="#475569" metalness={0.7} roughness={0.3} transparent opacity={0.5} />
      </mesh>
      {/* Piston rod */}
      <mesh ref={ref} position={[0, 2.2, 0]}>
        <cylinderGeometry args={[0.1, 0.1, 0.8, 8]} />
        <meshStandardMaterial color="#e2e8f0" metalness={0.9} roughness={0.1} />
      </mesh>
      {/* Piston head */}
      <mesh position={[0, 2.8, 0]}>
        <cylinderGeometry args={[0.2, 0.2, 0.1, 12]} />
        <meshStandardMaterial color="#94a3b8" metalness={0.8} roughness={0.2} />
      </mesh>
    </group>
  );
}

function RoboticArm({ speed }: { speed: number }) {
  const upperRef = useRef<THREE.Group>(null);
  const foreRef = useRef<THREE.Group>(null);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime * speed;
    if (upperRef.current) upperRef.current.rotation.z = Math.sin(t) * 0.4;
    if (foreRef.current) foreRef.current.rotation.z = Math.sin(t * 1.3 + 1) * 0.5;
  });
  return (
    <group position={[0, 1.8, 0]}>
      {/* Base pivot */}
      <mesh>
        <sphereGeometry args={[0.15, 12, 12]} />
        <meshStandardMaterial color="#f59e0b" metalness={0.7} roughness={0.3} />
      </mesh>
      {/* Upper arm */}
      <group ref={upperRef}>
        <mesh position={[0, 0.5, 0]}>
          <boxGeometry args={[0.12, 1, 0.12]} />
          <meshStandardMaterial color="#64748b" metalness={0.8} roughness={0.2} />
        </mesh>
        {/* Elbow */}
        <group ref={foreRef} position={[0, 1, 0]}>
          <mesh>
            <sphereGeometry args={[0.1, 8, 8]} />
            <meshStandardMaterial color="#f59e0b" metalness={0.7} roughness={0.3} />
          </mesh>
          <mesh position={[0, 0.4, 0]}>
            <boxGeometry args={[0.1, 0.8, 0.1]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.8} roughness={0.2} />
          </mesh>
          {/* Gripper */}
          <mesh position={[0, 0.85, 0]}>
            <coneGeometry args={[0.12, 0.2, 6]} />
            <meshStandardMaterial color="#ef4444" metalness={0.6} roughness={0.3} />
          </mesh>
        </group>
      </group>
    </group>
  );
}

function ReactorGlow({ speed }: { speed: number }) {
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const s = 1 + Math.sin(clock.elapsedTime * speed) * 0.15;
    ref.current.scale.set(s, s, s);
    (ref.current.material as THREE.MeshBasicMaterial).opacity =
      0.25 + Math.sin(clock.elapsedTime * speed * 1.5) * 0.15;
  });
  return (
    <group position={[0, 2.2, 0]}>
      {/* Core glow */}
      <mesh ref={ref}>
        <sphereGeometry args={[0.5, 16, 16]} />
        <meshBasicMaterial color="#a78bfa" transparent opacity={0.3} />
      </mesh>
      {/* Outer ring */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.7, 0.05, 8, 24]} />
        <meshStandardMaterial color="#6366f1" metalness={0.8} roughness={0.2} emissive="#4338ca" emissiveIntensity={0.3} />
      </mesh>
    </group>
  );
}

function ConveyorRollers({ speed }: { speed: number }) {
  const refs = useRef<(THREE.Mesh | null)[]>([]);
  useFrame((_, delta) => {
    refs.current.forEach(r => {
      if (r) r.rotation.z += delta * speed;
    });
  });
  return (
    <group position={[0, 1.6, 0]}>
      {[-0.6, -0.2, 0.2, 0.6].map((x, i) => (
        <mesh key={i} ref={el => { refs.current[i] = el; }} position={[x, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.1, 0.1, 0.6, 8]} />
          <meshStandardMaterial color="#64748b" metalness={0.8} roughness={0.2} />
        </mesh>
      ))}
      {/* Belt surface */}
      <mesh position={[0, 0.1, 0]}>
        <boxGeometry args={[1.5, 0.04, 0.55]} />
        <meshStandardMaterial color="#334155" roughness={0.9} metalness={0.1} />
      </mesh>
    </group>
  );
}

function AnimatedParts({ type, status }: { type: string; status: string }) {
  const cat = getMachineCategory(type);
  const speed = status === 'running' ? 3 : status === 'idle' ? 0.5 : 0;
  if (speed === 0) return null;

  switch (cat) {
    case 'rotary': return <SpinningFan speed={speed} />;
    case 'pump': return <PistonPump speed={speed} />;
    case 'arm': return <RoboticArm speed={speed} />;
    case 'reactor': return <ReactorGlow speed={speed} />;
    case 'conveyor': return <ConveyorRollers speed={speed} />;
  }
}

// ── Particle Effects ───────────────────────────────────────────
const PUFF_COUNT = 12;

function SteamPuff({ index }: { index: number }) {
  const ref = useRef<THREE.Mesh>(null);
  const offset = useRef(index * 0.4);
  const speed = useRef(0.5 + Math.random() * 0.3);
  const drift = useRef((Math.random() - 0.5) * 0.3);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = ((clock.elapsedTime + offset.current) * speed.current) % 2.5;
    const alpha = t < 0.3 ? t / 0.3 : Math.max(0, 1 - (t - 0.3) / 2.2);
    ref.current.position.set(drift.current * t, 2.8 + t * 1.2, drift.current * t * 0.5);
    const s = 0.15 + t * 0.2;
    ref.current.scale.set(s, s, s);
    (ref.current.material as THREE.MeshBasicMaterial).opacity = alpha * 0.55;
    ref.current.visible = alpha > 0.01;
  });

  return (
    <mesh ref={ref}>
      <sphereGeometry args={[1, 8, 8]} />
      <meshBasicMaterial color="#e2e8f0" transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

function SteamParticles() {
  return (
    <group>
      {Array.from({ length: PUFF_COUNT }, (_, i) => <SteamPuff key={i} index={i} />)}
    </group>
  );
}

const SPARK_COUNT = 10;

function Spark({ index }: { index: number }) {
  const ref = useRef<THREE.Mesh>(null);
  const offset = useRef(index * 0.15);
  const dirX = useRef((Math.random() - 0.5) * 3);
  const dirZ = useRef((Math.random() - 0.5) * 3);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = ((clock.elapsedTime + offset.current) * 2.5) % 0.8;
    const alpha = Math.max(0, 1 - t / 0.8);
    ref.current.position.set(dirX.current * t, 2.5 + t * 2.5, dirZ.current * t);
    const s = 0.06 * alpha;
    ref.current.scale.set(s, s, s);
    (ref.current.material as THREE.MeshBasicMaterial).opacity = alpha * 0.9;
    ref.current.visible = alpha > 0.05;
  });

  return (
    <mesh ref={ref}>
      <sphereGeometry args={[1, 4, 4]} />
      <meshBasicMaterial color="#fb923c" transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

function SparkParticles() {
  return (
    <group>
      {Array.from({ length: SPARK_COUNT }, (_, i) => <Spark key={i} index={i} />)}
    </group>
  );
}

function HeatShimmer() {
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = clock.elapsedTime;
    const s = 1 + Math.sin(t * 4) * 0.15;
    ref.current.scale.set(s, 1 + Math.sin(t * 3) * 0.25, s);
    (ref.current.material as THREE.MeshBasicMaterial).opacity = 0.1 + Math.sin(t * 5) * 0.06;
  });
  return (
    <mesh ref={ref} position={[0, 3.2, 0]}>
      <cylinderGeometry args={[0.8, 0.4, 2, 12, 1, true]} />
      <meshBasicMaterial color="#f97316" transparent opacity={0.1} side={THREE.DoubleSide} depthWrite={false} />
    </mesh>
  );
}

function MachineParticles({ type, status }: { type: string; status: string }) {
  if (status !== 'running') return null;
  const cat = getMachineCategory(type);
  switch (cat) {
    case 'arm': return <SparkParticles />;
    case 'reactor': return <HeatShimmer />;
    default: return <SteamParticles />;
  }
}

// ── Glow Ring ──────────────────────────────────────────────────
function GlowRing({ status, radius = 1.2 }: { status: string; radius?: number }) {
  const ref = useRef<THREE.Mesh>(null);
  const color = STATUS_COLORS[status] || '#666';

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const s = 1 + Math.sin(clock.elapsedTime * 2) * 0.08;
    ref.current.scale.set(s, s, 1);
    (ref.current.material as THREE.MeshBasicMaterial).opacity =
      0.3 + Math.sin(clock.elapsedTime * 3) * 0.15;
  });

  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
      <ringGeometry args={[radius * 0.85, radius, 32]} />
      <meshBasicMaterial color={color} transparent opacity={0.4} side={THREE.DoubleSide} />
    </mesh>
  );
}

// ── Floating Label ─────────────────────────────────────────────
function MachineLabel({ name, status, sensorCount, selected }: {
  name: string; status: string; sensorCount: number; selected: boolean;
}) {
  const color = STATUS_COLORS[status] || '#666';
  return (
    <Html position={[0, 3.5, 0]} center distanceFactor={12} style={{ pointerEvents: 'none' }}>
      <div style={{
        background: selected ? 'rgba(124,58,237,0.9)' : 'rgba(15,15,25,0.85)',
        backdropFilter: 'blur(8px)',
        border: `1px solid ${selected ? '#a78bfa' : 'rgba(255,255,255,0.15)'}`,
        borderRadius: 8, padding: '6px 12px', textAlign: 'center',
        minWidth: 100, transition: 'all 0.3s',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{name}</div>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: color,
            boxShadow: `0 0 6px ${color}`, display: 'inline-block',
          }} />
          <span style={{ fontSize: 9, color: '#ccc', textTransform: 'uppercase' }}>{status}</span>
          <span style={{ fontSize: 9, color: '#888' }}>· {sensorCount}s</span>
        </div>
      </div>
    </Html>
  );
}

// ── Hotspot helpers ────────────────────────────────────────────
function computeMachineHealth(machine: any, sensorData: any) {
  const sensors = machine.sensors || [];
  let worstPct = 0, critCount = 0, warnCount = 0;
  const sensorStats: { name: string; value: number; unit: string; pct: number; status: string; min: number; max: number }[] = [];
  sensors.forEach((s: any) => {
    const val = sensorData?.[machine.machineId]?.[s.sensorId] ?? s.value;
    const range = s.max - s.min;
    const pct = range > 0 ? ((val - s.min) / range) * 100 : 0;
    const status = pct > 95 ? 'critical' : pct > 80 ? 'warning' : 'ok';
    if (pct > worstPct) worstPct = pct;
    if (status === 'critical') critCount++;
    if (status === 'warning') warnCount++;
    sensorStats.push({ name: s.name, value: val, unit: s.unit, pct, status, min: s.min, max: s.max });
  });
  const riskScore = Math.min(99, Math.round(worstPct > 80 ? 30 + (worstPct - 80) * 3.5 : worstPct * 0.3));
  const rul = Math.max(1, Math.round(90 * (1 - riskScore / 100)));
  const riskLevel = riskScore > 70 ? 'CRITICAL' : riskScore > 50 ? 'HIGH' : riskScore > 30 ? 'MEDIUM' : 'LOW';
  const riskColor = riskScore > 70 ? '#f87171' : riskScore > 50 ? '#f97316' : riskScore > 30 ? '#fbbf24' : '#34d399';
  const hotspotColor = critCount > 0 ? '#f87171' : warnCount > 0 ? '#fbbf24' : '#34d399';
  return { sensorStats, riskScore, rul, riskLevel, riskColor, hotspotColor, critCount, warnCount, alertCount: critCount + warnCount };
}

// ── 3D Hotspot Marker ──────────────────────────────────────────
function Hotspot3D({ position, color, alertCount, onClick, isOpen }: {
  position: [number, number, number]; color: string; alertCount: number; onClick: () => void; isOpen: boolean;
}) {
  const outerRef = useRef<THREE.Mesh>(null);
  const innerRef = useRef<THREE.Mesh>(null);
  const pulseSpeed = alertCount > 0 ? 4 + alertCount * 1.5 : 1.5;
  useFrame(({ clock }) => {
    const t = clock.elapsedTime * pulseSpeed;
    if (outerRef.current) {
      const s = 1 + Math.sin(t) * 0.3;
      outerRef.current.scale.set(s, s, s);
      (outerRef.current.material as THREE.MeshBasicMaterial).opacity = 0.15 + Math.sin(t) * 0.1;
    }
    if (innerRef.current) {
      const s = 1 + Math.sin(t * 1.5) * 0.1;
      innerRef.current.scale.set(s, s, s);
    }
  });
  return (
    <group position={[position[0], position[1] + 4.2, position[2]]}>
      <mesh ref={outerRef} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.35, 0.55, 24]} />
        <meshBasicMaterial color={color} transparent opacity={0.2} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      <mesh ref={innerRef} onClick={(e) => { e.stopPropagation(); onClick(); }}>
        <sphereGeometry args={[0.18, 16, 16]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <pointLight color={color} intensity={isOpen ? 1.5 : 0.5} distance={4} />
      <mesh position={[0, -0.8, 0]}>
        <cylinderGeometry args={[0.015, 0.015, 1.4, 4]} />
        <meshBasicMaterial color={color} transparent opacity={0.4} />
      </mesh>
      {alertCount > 0 && (
        <Html position={[0.35, 0.25, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={{ background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 8px rgba(239,68,68,0.6)' }}>{alertCount}</div>
        </Html>
      )}
    </group>
  );
}

// ── Hotspot Popup (rich overlay) ───────────────────────────────
function HotspotPopup({ machine, health, onClose }: {
  machine: any; health: ReturnType<typeof computeMachineHealth>; onClose: () => void;
}) {
  const statusColor = STATUS_COLORS[machine.status] || '#666';
  return (
    <Html position={[0, 5.5, 0]} center style={{ pointerEvents: 'auto' }}>
      <div className="hotspot-popup" onClick={(e) => e.stopPropagation()}>
        <div className="hp-header">
          <div className="hp-title-row">
            <span className="hp-name">{machine.name}</span>
            <button className="hp-close" onClick={onClose}>✕</button>
          </div>
          <div className="hp-meta">
            <span className="hp-type">{machine.type}</span>
            <span className="hp-status" style={{ color: statusColor, borderColor: statusColor }}>● {machine.status.toUpperCase()}</span>
          </div>
        </div>
        <div className="hp-risk-section">
          <div className="hp-risk-gauge">
            <svg width="64" height="64" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
              <circle cx="32" cy="32" r="28" fill="none" stroke={health.riskColor} strokeWidth="5" strokeDasharray={`${health.riskScore * 1.76} 176`} strokeLinecap="round" transform="rotate(-90 32 32)" />
            </svg>
            <div className="hp-risk-value" style={{ color: health.riskColor }}>{health.riskScore}%</div>
          </div>
          <div className="hp-risk-info">
            <div className="hp-risk-label">Failure Risk</div>
            <div className="hp-risk-level" style={{ color: health.riskColor }}>{health.riskLevel}</div>
            <div className="hp-rul">RUL: <strong>{health.rul}d</strong></div>
          </div>
          {health.alertCount > 0 && (
            <div className="hp-alert-count"><span>⚠️</span><span>{health.alertCount} alert{health.alertCount > 1 ? 's' : ''}</span></div>
          )}
        </div>
        <div className="hp-sensors">
          <div className="hp-section-title">📊 Live Sensors</div>
          {health.sensorStats.slice(0, 6).map((s, i) => (
            <div key={i} className="hp-sensor-row">
              <div className="hp-sensor-header">
                <span className="hp-sensor-name">{s.name}</span>
                <span className="hp-sensor-val" style={{ color: s.status === 'critical' ? '#f87171' : s.status === 'warning' ? '#fbbf24' : '#34d399' }}>{s.value.toFixed(1)}{s.unit}</span>
              </div>
              <div className="hp-sensor-bar">
                <div className="hp-sensor-fill" style={{ width: `${Math.min(s.pct, 100)}%`, background: s.status === 'critical' ? '#f87171' : s.status === 'warning' ? '#fbbf24' : '#34d399' }} />
                <div className="hp-sensor-warn-line" />
                <div className="hp-sensor-crit-line" />
              </div>
              <div className="hp-sensor-range">{s.min} — {s.max}{s.unit}</div>
            </div>
          ))}
        </div>
      </div>
    </Html>
  );
}

// ── Machine Hotspot (combines marker + popup) ──────────────────
function MachineHotspot({ machine, position, sensorData, openHotspot, setOpenHotspot }: {
  machine: any; position: [number, number, number]; sensorData: any;
  openHotspot: string | null; setOpenHotspot: (id: string | null) => void;
}) {
  const health = useMemo(() => computeMachineHealth(machine, sensorData), [machine, sensorData]);
  const isOpen = openHotspot === machine.machineId;
  return (
    <group>
      <Hotspot3D position={position} color={health.hotspotColor} alertCount={health.alertCount} isOpen={isOpen} onClick={() => setOpenHotspot(isOpen ? null : machine.machineId)} />
      {isOpen && (
        <group position={[position[0], position[1], position[2]]}>
          <HotspotPopup machine={machine} health={health} onClose={() => setOpenHotspot(null)} />
        </group>
      )}
    </group>
  );
}

// ── Sensor HUD (shows on selected machine) ─────────────────────
function SensorHUD({ machine, sensorData }: { machine: any; sensorData: any }) {
  const sensors = machine.sensors || [];
  if (sensors.length === 0) return null;

  return (
    <Html position={[2.5, 2, 0]} style={{ pointerEvents: 'none' }}>
      <div style={{
        background: 'rgba(10,10,20,0.92)', backdropFilter: 'blur(10px)',
        border: '1px solid rgba(124,58,237,0.4)', borderRadius: 10,
        padding: 10, minWidth: 160, maxWidth: 200,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#a78bfa', marginBottom: 6 }}>
          📊 LIVE SENSORS
        </div>
        {sensors.slice(0, 5).map((s: any) => {
          const val = sensorData?.[machine.machineId]?.[s.sensorId] ?? s.value;
          const pct = ((val - s.min) / (s.max - s.min)) * 100;
          const clr = pct > 95 ? '#f87171' : pct > 80 ? '#fbbf24' : '#34d399';
          return (
            <div key={s.sensorId} style={{ marginBottom: 5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9 }}>
                <span style={{ color: '#ccc' }}>{s.name}</span>
                <span style={{ color: clr, fontWeight: 700, fontFamily: 'monospace' }}>
                  {val.toFixed(1)}{s.unit}
                </span>
              </div>
              <div style={{ height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, marginTop: 2 }}>
                <div style={{
                  width: `${Math.min(pct, 100)}%`, height: '100%',
                  background: clr, borderRadius: 2, transition: 'width 0.5s',
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </Html>
  );
}

// ── Camera Controller ──────────────────────────────────────────
function CameraController({ target }: { target: THREE.Vector3 | null }) {
  const { camera } = useThree();
  const targetRef = useRef<THREE.Vector3 | null>(null);

  useEffect(() => {
    if (target) targetRef.current = target.clone().add(new THREE.Vector3(5, 4, 5));
  }, [target]);

  useFrame(() => {
    if (!targetRef.current) return;
    camera.position.lerp(targetRef.current, 0.03);
    camera.lookAt(target || new THREE.Vector3(0, 0, 0));
  });

  return null;
}

// ── Machine Model ──────────────────────────────────────────────
function MachineModel({ machine, index, total, onClick, selected, sensorData, hotspotOpen }: any) {
  const modelPath = getModelPath(machine.type || 'pump');
  const baseScale = getModelScale(modelPath);
  const ref = useRef<THREE.Group>(null);

  // Circular layout for ≤6, grid for more
  const pos = useMemo((): [number, number, number] => {
    if (total <= 6) {
      const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
      const r = 5 + total * 0.5;
      return [Math.cos(angle) * r, 0.5, Math.sin(angle) * r];
    }
    const cols = Math.ceil(Math.sqrt(total));
    const spacing = 6;
    const x = (index % cols) * spacing - (cols * spacing) / 2 + spacing / 2;
    const z = Math.floor(index / cols) * spacing - spacing;
    return [x, 0.5, z];
  }, [index, total]);

  // Hover bob animation
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const bob = selected ? Math.sin(clock.elapsedTime * 2) * 0.1 : 0;
    ref.current.position.y = pos[1] + bob;
    const targetScale = selected ? baseScale * 1.15 : baseScale;
    ref.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.1);
  });

  let scene: THREE.Object3D | null = null;
  try {
    const gltf = useGLTF(modelPath);
    if (gltf?.scene) {
      scene = gltf.scene.clone();
      scene.traverse((child: any) => {
        if (child.isMesh && child.material) {
          const mat = child.material.clone();
          const hex = machine.status === 'running' ? 0x1acc4d
            : machine.status === 'idle' ? 0xffcc00 : 0xff3333;
          mat.color.setHex(hex);
          mat.roughness = 0.4;
          mat.metalness = 0.6;
          child.material = mat;
          child.castShadow = true;
        }
      });
    }
  } catch { /* fallback below */ }

  return (
    <group ref={ref} position={pos}>
      {/* Glow ring on floor */}
      <GlowRing status={machine.status} radius={1.5} />

      {/* Model or fallback */}
      {scene ? (
        <primitive
          object={scene}
          scale={baseScale}
          onClick={(e: any) => { e.stopPropagation(); onClick(machine, new THREE.Vector3(...pos)); }}
        />
      ) : (
        <mesh
          onClick={(e: any) => { e.stopPropagation(); onClick(machine, new THREE.Vector3(...pos)); }}
        >
          <cylinderGeometry args={[0.8, 0.8, 2.5, 16]} />
          <meshStandardMaterial
            color={STATUS_COLORS[machine.status] || '#666'}
            roughness={0.4} metalness={0.6}
          />
        </mesh>
      )}

      {/* Animated parts */}
      <AnimatedParts type={machine.type || 'pump'} status={machine.status} />

      {/* Particle effects */}
      <MachineParticles type={machine.type || 'pump'} status={machine.status} />

      {/* Floating label */}
      <MachineLabel
        name={machine.name}
        status={machine.status}
        sensorCount={machine.sensors?.length || 0}
        selected={selected}
      />

      {/* Sensor HUD on selected (hidden when hotspot popup is open) */}
      {selected && !hotspotOpen && <SensorHUD machine={machine} sensorData={sensorData} />}
    </group>
  );
}

// ── Conveyor Belts between machines ────────────────────────────
const BOX_COLORS = ['#8B6914', '#3b82f6', '#10b981', '#ef4444', '#f59e0b'];
const BELT_Y = 0.22;
const BOXES_PER_BELT = 3;

function getMachinePos(index: number, total: number): [number, number, number] {
  if (total <= 6) {
    const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
    const r = 5 + total * 0.5;
    return [Math.cos(angle) * r, 0.5, Math.sin(angle) * r];
  }
  const cols = Math.ceil(Math.sqrt(total));
  const spacing = 6;
  return [
    (index % cols) * spacing - (cols * spacing) / 2 + spacing / 2,
    0.5,
    Math.floor(index / cols) * spacing - spacing,
  ];
}

function MovingBox({ from, to, offset, speed, color }: {
  from: THREE.Vector3; to: THREE.Vector3; offset: number; speed: number; color: string;
}) {
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = ((clock.elapsedTime * speed + offset) % 1 + 1) % 1;
    ref.current.position.lerpVectors(from, to, t);
    ref.current.rotation.y = clock.elapsedTime * 0.5;
  });
  return (
    <mesh ref={ref}>
      <boxGeometry args={[0.32, 0.26, 0.32]} />
      <meshStandardMaterial color={color} roughness={0.6} metalness={0.3} />
    </mesh>
  );
}

function ConveyorBelt({ from, to, active }: {
  from: [number, number, number]; to: [number, number, number]; active: boolean;
}) {
  const a = new THREE.Vector3(from[0], BELT_Y, from[2]);
  const b = new THREE.Vector3(to[0], BELT_Y, to[2]);
  const dir = b.clone().sub(a);
  const len = dir.length();
  // Shorten belt so it doesn't overlap machine glow rings
  const shrink = 2;
  const normDir = dir.clone().normalize();
  const beltA = a.clone().add(normDir.clone().multiplyScalar(shrink));
  const beltB = b.clone().sub(normDir.clone().multiplyScalar(shrink));
  const beltMid = beltA.clone().add(beltB).multiplyScalar(0.5);
  const beltLen = Math.max(0, len - shrink * 2);
  const angle = Math.atan2(dir.x, dir.z);

  if (beltLen < 0.5) return null;

  return <ConveyorBeltInner beltA={beltA} beltB={beltB} beltMid={beltMid} beltLen={beltLen} angle={angle} active={active} />;
}

function ConveyorBeltInner({ beltA, beltB, beltMid, beltLen, angle, active }: {
  beltA: THREE.Vector3; beltB: THREE.Vector3; beltMid: THREE.Vector3;
  beltLen: number; angle: number; active: boolean;
}) {
  const rollerCount = Math.max(2, Math.floor(beltLen / 1.2));
  const rollerRefs = useRef<(THREE.Mesh | null)[]>([]);
  useFrame((_, delta) => {
    if (!active) return;
    rollerRefs.current.forEach(r => { if (r) r.rotation.z += delta * 4; });
  });

  return (
    <group>
      {/* Belt surface */}
      <mesh position={[beltMid.x, BELT_Y, beltMid.z]} rotation={[0, angle, 0]}>
        <boxGeometry args={[1.1, 0.06, beltLen]} />
        <meshStandardMaterial color="#e8e8ec" roughness={0.4} metalness={0.15} />
      </mesh>

      {/* Side rails */}
      {[-0.6, 0.6].map((side, si) => (
        <mesh key={si} position={[
          beltMid.x + Math.cos(angle + Math.PI / 2) * side,
          BELT_Y + 0.08,
          beltMid.z + Math.sin(angle + Math.PI / 2) * side * -1,
        ]} rotation={[0, angle, 0]}>
          <boxGeometry args={[0.06, 0.12, beltLen]} />
          <meshStandardMaterial color="#d97706" roughness={0.5} metalness={0.6} />
        </mesh>
      ))}

      {/* Rollers */}
      {Array.from({ length: rollerCount }, (_, i) => {
        const t = (i + 0.5) / rollerCount;
        const pos = beltA.clone().lerp(beltB, t);
        return (
          <mesh key={i} ref={el => { rollerRefs.current[i] = el; }}
            position={[pos.x, BELT_Y - 0.04, pos.z]}
            rotation={[0, angle + Math.PI / 2, 0]}>
            <cylinderGeometry args={[0.06, 0.06, 1.0, 8]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.7} roughness={0.3} />
          </mesh>
        );
      })}

      {/* Moving boxes */}
      {active && Array.from({ length: BOXES_PER_BELT }, (_, i) => (
        <MovingBox
          key={i}
          from={new THREE.Vector3(beltA.x, BELT_Y + 0.12, beltA.z)}
          to={new THREE.Vector3(beltB.x, BELT_Y + 0.12, beltB.z)}
          offset={i / BOXES_PER_BELT}
          speed={0.2}
          color={BOX_COLORS[i % BOX_COLORS.length]}
        />
      ))}
    </group>
  );
}

function ConveyorBelts({ machines }: { machines: any[] }) {
  const total = machines.length;
  if (total < 2) return null;

  // Build pairs: adjacent in ring (≤6) or sequential in grid
  const pairs: [number, number][] = [];
  for (let i = 0; i < total - 1; i++) pairs.push([i, i + 1]);
  if (total <= 6 && total > 2) pairs.push([total - 1, 0]); // close the ring

  return (
    <group>
      {pairs.map(([ai, bi]) => {
        const posA = getMachinePos(ai, total);
        const posB = getMachinePos(bi, total);
        const bothOk = machines[ai].status !== 'fault' && machines[bi].status !== 'fault';
        const anyRunning = machines[ai].status === 'running' || machines[bi].status === 'running';
        return (
          <ConveyorBelt
            key={`${ai}-${bi}`}
            from={posA}
            to={posB}
            active={bothOk && anyRunning}
          />
        );
      })}
    </group>
  );
}

// ── Background Filler Machines ─────────────────────────────────
function BgTank({ position, color, h = 3, r = 0.6 }: { position: [number, number, number]; color: string; h?: number; r?: number }) {
  return (
    <group position={position}>
      <mesh position={[0, h / 2, 0]}>
        <cylinderGeometry args={[r, r, h, 16]} />
        <meshStandardMaterial color={color} roughness={0.5} metalness={0.6} />
      </mesh>
      {/* Top dome */}
      <mesh position={[0, h, 0]}>
        <sphereGeometry args={[r, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={color} roughness={0.5} metalness={0.6} />
      </mesh>
      {/* Pipe stub */}
      <mesh position={[r + 0.15, h * 0.6, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.08, 0.08, 0.5, 8]} />
        <meshStandardMaterial color="#64748b" metalness={0.8} roughness={0.2} />
      </mesh>
    </group>
  );
}

function BgCabinet({ position, rot = 0 }: { position: [number, number, number]; rot?: number }) {
  return (
    <group position={position} rotation={[0, rot, 0]}>
      <mesh position={[0, 1, 0]}>
        <boxGeometry args={[1.2, 2, 0.5]} />
        <meshStandardMaterial color="#334155" roughness={0.6} metalness={0.5} />
      </mesh>
      {/* Screen */}
      <mesh position={[0, 1.3, 0.26]}>
        <planeGeometry args={[0.8, 0.5]} />
        <meshBasicMaterial color="#22d3ee" transparent opacity={0.6} />
      </mesh>
      {/* Indicator lights */}
      {[-0.3, 0, 0.3].map((x, i) => (
        <mesh key={i} position={[x, 0.6, 0.26]}>
          <circleGeometry args={[0.04, 8]} />
          <meshBasicMaterial color={i === 0 ? '#10b981' : i === 1 ? '#fbbf24' : '#ef4444'} />
        </mesh>
      ))}
    </group>
  );
}

function BgPipeRack({ position, length = 6 }: { position: [number, number, number]; length?: number }) {
  return (
    <group position={position}>
      {/* Supports */}
      {[-length / 2, 0, length / 2].map((x, i) => (
        <group key={i}>
          <mesh position={[x, 1.5, 0]}>
            <boxGeometry args={[0.12, 3, 0.12]} />
            <meshStandardMaterial color="#475569" metalness={0.7} roughness={0.3} />
          </mesh>
          <mesh position={[x, 3, 0]}>
            <boxGeometry args={[0.5, 0.1, 0.12]} />
            <meshStandardMaterial color="#475569" metalness={0.7} roughness={0.3} />
          </mesh>
        </group>
      ))}
      {/* Pipes */}
      {[2.2, 2.6, 3.0].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.06, 0.06, length, 8]} />
          <meshStandardMaterial color={['#94a3b8', '#f59e0b', '#3b82f6'][i]} metalness={0.7} roughness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

function BgMotor({ position, rot = 0 }: { position: [number, number, number]; rot?: number }) {
  const shaftRef = useRef<THREE.Mesh>(null);
  useFrame((_, d) => { if (shaftRef.current) shaftRef.current.rotation.z += d * 4; });
  return (
    <group position={position} rotation={[0, rot, 0]}>
      {/* Motor body */}
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[0.8, 0.8, 1.2]} />
        <meshStandardMaterial color="#1e3a5f" roughness={0.5} metalness={0.7} />
      </mesh>
      {/* Shaft */}
      <mesh ref={shaftRef} position={[0, 0.5, 0.8]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.06, 0.06, 0.6, 8]} />
        <meshStandardMaterial color="#94a3b8" metalness={0.9} roughness={0.1} />
      </mesh>
      {/* Base */}
      <mesh position={[0, 0.05, 0]}>
        <boxGeometry args={[1, 0.1, 1.4]} />
        <meshStandardMaterial color="#334155" roughness={0.7} metalness={0.4} />
      </mesh>
    </group>
  );
}

function BgBarrelStack({ position }: { position: [number, number, number] }) {
  const colors = ['#1e40af', '#b91c1c', '#15803d', '#1e40af'];
  return (
    <group position={position}>
      {/* Bottom row */}
      {[-0.55, 0, 0.55].map((x, i) => (
        <mesh key={`b-${i}`} position={[x, 0.45, 0]} rotation={[0, 0, 0]}>
          <cylinderGeometry args={[0.25, 0.25, 0.9, 10]} />
          <meshStandardMaterial color={colors[i]} roughness={0.7} metalness={0.3} />
        </mesh>
      ))}
      {/* Top row */}
      {[-0.28, 0.28].map((x, i) => (
        <mesh key={`t-${i}`} position={[x, 1.35, 0]}>
          <cylinderGeometry args={[0.25, 0.25, 0.9, 10]} />
          <meshStandardMaterial color={colors[i + 2]} roughness={0.7} metalness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

function BackgroundMachines() {
  return (
    <group>
      {/* Back-left: tank farm */}
      <BgTank position={[-16, 0, -12]} color="#1e3a5f" h={4} r={0.9} />
      <BgTank position={[-13, 0, -12]} color="#1e3a5f" h={3.5} r={0.7} />
      <BgTank position={[-14.5, 0, -10]} color="#374151" h={2.5} r={0.5} />

      {/* Back-right: tall silo + small tank */}
      <BgTank position={[14, 0, -12]} color="#4a2040" h={5} r={1} />
      <BgTank position={[16.5, 0, -11]} color="#374151" h={3} r={0.6} />

      {/* Left wall: control panels */}
      <BgCabinet position={[-18, 0, -4]} rot={Math.PI / 2} />
      <BgCabinet position={[-18, 0, 0]} rot={Math.PI / 2} />
      <BgCabinet position={[-18, 0, 4]} rot={Math.PI / 2} />

      {/* Right wall: pipe rack */}
      <BgPipeRack position={[17, 0, -6]} length={8} />
      <BgPipeRack position={[17, 0, 4]} length={6} />

      {/* Back wall: motors + barrels */}
      <BgMotor position={[-6, 0, -13]} />
      <BgMotor position={[-3, 0, -13]} />
      <BgMotor position={[6, 0, -13]} rot={Math.PI} />
      <BgBarrelStack position={[10, 0, -13]} />
      <BgBarrelStack position={[-10, 0, 8]} />

      {/* Front corners: small tanks */}
      <BgTank position={[-16, 0, 10]} color="#1e3a5f" h={2} r={0.5} />
      <BgTank position={[16, 0, 10]} color="#374151" h={2.5} r={0.5} />

      {/* Scattered motors near walkways */}
      <BgMotor position={[12, 0, 2]} rot={-Math.PI / 2} />
      <BgMotor position={[-8, 0, 8]} rot={Math.PI / 4} />
    </group>
  );
}

// ── Uploaded Model Scene (renders the user's GLB as-is) ──────
function UploadedModelScene({ url, machines, selectedMachine, sensorData, onSelect }: {
  url: string; machines: any[]; selectedMachine: any; sensorData: any;
  onSelect: (m: any, pos: THREE.Vector3) => void;
}) {
  const gltf = useGLTF(url);
  const groupRef = useRef<THREE.Group>(null);

  // Auto-scale and center the model
  useEffect(() => {
    if (!groupRef.current || !gltf.scene) return;
    const box = new THREE.Box3().setFromObject(gltf.scene);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = 15 / maxDim; // fit within ~15 units
    groupRef.current.scale.setScalar(scale);
    const center = box.getCenter(new THREE.Vector3());
    groupRef.current.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
  }, [gltf.scene]);

  return (
    <group ref={groupRef}>
      <primitive
        object={gltf.scene}
        onClick={(e: any) => {
          e.stopPropagation();
          // Find which machine node was clicked
          let obj = e.object;
          while (obj && obj.parent) {
            const match = machines.find((m: any) => {
              const nodeName = (m as any).nodeName || m.name;
              return obj.name && (obj.name === nodeName || obj.name.includes(nodeName) || nodeName.includes(obj.name));
            });
            if (match) {
              const wp = new THREE.Vector3();
              e.object.getWorldPosition(wp);
              onSelect(match, wp);
              return;
            }
            obj = obj.parent;
          }
          // Fallback: select first machine
          if (machines.length > 0) {
            const wp = new THREE.Vector3();
            e.object.getWorldPosition(wp);
            onSelect(machines[0], wp);
          }
        }}
      />
      {/* Sensor HUDs floating above machine positions */}
      {machines.map((m: any, i: number) => {
        const isSelected = selectedMachine?.machineId === m.machineId;
        return (
          <group key={m.machineId || i}>
            {isSelected && <SensorHUD machine={m} sensorData={sensorData} />}
          </group>
        );
      })}
    </group>
  );
}

// ── Main Component ─────────────────────────────────────────────
const DigitalTwinViewer: React.FC = () => {
  const { getActiveTwin } = useTwin();
  const activeTwin = getActiveTwin();
  const [selectedMachine, setSelectedMachine] = useState<any>(null);
  const [cameraTarget, setCameraTarget] = useState<THREE.Vector3 | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [sensorData, setSensorData] = useState<any>({});
  const [audioOn, setAudioOn] = useState(false);
  const [modelBlobUrl, setModelBlobUrl] = useState<string | null>(null);
  const [openHotspot, setOpenHotspot] = useState<string | null>(null);
  const [hotspotsVisible, setHotspotsVisible] = useState(true);
  const hasUploadedModel = !!activeTwin?.modelUrl;
  useFactoryAmbience(audioOn);
  const blobUrlCacheRef = useRef<Map<string, string>>(new Map());

  // Load uploaded GLB from IndexedDB
  useEffect(() => {
    if (!activeTwin?.modelUrl?.startsWith('indexeddb://')) {
      setModelBlobUrl(null);
      return;
    }
    const twinId = activeTwin.id;
    // Check cache first
    if (blobUrlCacheRef.current.has(twinId)) {
      setModelBlobUrl(blobUrlCacheRef.current.get(twinId) || null);
      return;
    }
    let revoked = false;
    openModelDB().then(db => loadModel(db, twinId)).then(result => {
      if (result && !revoked) {
        const blob = new Blob([result.data], { type: 'model/gltf-binary' });
        const url = URL.createObjectURL(blob);
        blobUrlCacheRef.current.set(twinId, url);
        setModelBlobUrl(url);
      }
    }).catch(() => {});
    return () => { revoked = true; };
  }, [activeTwin?.id, activeTwin?.modelUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Simulate sensor values
  useEffect(() => {
    if (!activeTwin?.machines) return;
    const init: any = {};
    activeTwin.machines.forEach((m: any) => {
      init[m.machineId] = {};
      m.sensors.forEach((s: any) => { init[m.machineId][s.sensorId] = s.value; });
    });
    setSensorData(init);

    const iv = setInterval(() => {
      setSensorData((prev: any) => {
        const next = { ...prev };
        activeTwin.machines.forEach((m: any) => {
          if (!next[m.machineId]) next[m.machineId] = {};
          m.sensors.forEach((s: any) => {
            const cur = next[m.machineId][s.sensorId] || s.value;
            const drift = (Math.random() - 0.5) * 2;
            next[m.machineId][s.sensorId] = Math.max(s.min, Math.min(s.max, cur + drift));
          });
        });
        return next;
      });
    }, 2000);
    return () => clearInterval(iv);
  }, [activeTwin]);

  const handleClick = (machine: any, pos: THREE.Vector3) => {
    setSelectedMachine(machine);
    setCameraTarget(pos);
    setShowDetails(true);
  };

  if (!activeTwin || !activeTwin.machines?.length) {
    return (
      <div className="custom-3d-viewer-page">
        <div className="page-header">
          <h1>🎬 3D Scene</h1>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
          {!activeTwin ? 'No active twin selected' : 'No machines configured'}
        </div>
      </div>
    );
  }

  const machines = activeTwin.machines;
  const statusColor = selectedMachine
    ? (STATUS_COLORS[selectedMachine.status] || '#666') : '#666';

  return (
    <div className="custom-3d-viewer-page">
      <div className="page-header">
        <h1>🎬 {activeTwin.name || "Digital Twin"}</h1>
        <span className="badge-green">● {machines.length} Machines</span>
        <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 12 }}>
          Health: {activeTwin.healthScore || 100}%
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn-mini" onClick={() => { setCameraTarget(null); setSelectedMachine(null); setShowDetails(false); }}>
            🏠 Overview
          </button>
          <button className="btn-mini" onClick={() => window.location.reload()}>
            🔄 Reset
          </button>
          <button className={`btn-mini ${hotspotsVisible ? 'btn-mini-active' : ''}`} onClick={() => { setHotspotsVisible(p => !p); if (hotspotsVisible) setOpenHotspot(null); }}>
            {hotspotsVisible ? '📍 Hotspots' : '○ Hotspots'}
          </button>
          <button className="btn-mini" onClick={() => setAudioOn(p => !p)}>
            {audioOn ? '🔊 Mute' : '🔇 Sound'}
          </button>
        </div>
      </div>

      <div className="viewer-container">
        <Canvas
          shadows
          camera={{ position: [15, 12, 15], fov: 50 }}
          style={{ background: '#0a0a14' }}
        >
          {/* Lighting */}
          <ambientLight intensity={0.3} />
          <directionalLight position={[10, 15, 10]} intensity={0.8} castShadow
            shadow-mapSize-width={2048} shadow-mapSize-height={2048} />
          <pointLight position={[-8, 8, -8]} intensity={0.3} color="#4060ff" />
          <pointLight position={[8, 6, -8]} intensity={0.2} color="#a78bfa" />

          {/* Fog */}
          <fog attach="fog" args={['#0a0a14', 30, 70]} />

          <OrbitControls
            enableDamping dampingFactor={0.05}
            minDistance={5} maxDistance={40}
            maxPolarAngle={Math.PI / 2.1}
          />

          <CameraController target={cameraTarget} />

          {/* Render uploaded GLB or default factory */}
          {hasUploadedModel && modelBlobUrl ? (
            <Suspense fallback={null}>
              <UploadedModelScene
                url={modelBlobUrl}
                machines={machines}
                selectedMachine={selectedMachine}
                sensorData={sensorData}
                onSelect={handleClick}
              />
            </Suspense>
          ) : (
            <>
              <FactoryFloor />
              <FactoryWalls />
              <FactoryPillars />
              <CeilingBeams />
              <OverheadLights />
              <OverheadCrane />
              <Catwalks />
              <ConveyorBelts machines={machines} />
              <BackgroundMachines />

              <Suspense fallback={null}>
                {machines.map((m: any, i: number) => (
                  <MachineModel
                    key={m.machineId || i}
                    machine={m}
                    index={i}
                    total={machines.length}
                    onClick={handleClick}
                    selected={selectedMachine?.machineId === m.machineId}
                    sensorData={sensorData}
                    hotspotOpen={openHotspot === m.machineId}
                  />
                ))}
              </Suspense>

              {/* Hotspot markers */}
              {hotspotsVisible && machines.map((m: any, i: number) => {
                const t = machines.length;
                let pos: [number, number, number];
                if (t <= 6) {
                  const angle = (i / t) * Math.PI * 2 - Math.PI / 2;
                  const r = 5 + t * 0.5;
                  pos = [Math.cos(angle) * r, 0.5, Math.sin(angle) * r];
                } else {
                  const cols = Math.ceil(Math.sqrt(t));
                  const spacing = 6;
                  pos = [(i % cols) * spacing - (cols * spacing) / 2 + spacing / 2, 0.5, Math.floor(i / cols) * spacing - spacing];
                }
                return (
                  <MachineHotspot
                    key={`hs-${m.machineId || i}`}
                    machine={m}
                    position={pos}
                    sensorData={sensorData}
                    openHotspot={openHotspot}
                    setOpenHotspot={setOpenHotspot}
                  />
                );
              })}
            </>
          )}

          {/* Ground plane for uploaded models */}
          {hasUploadedModel && (
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
              <planeGeometry args={[50, 50]} />
              <meshStandardMaterial color="#1a1a2c" roughness={0.95} metalness={0.05} />
            </mesh>
          )}
        </Canvas>

        {/* Detail Panel */}
        {showDetails && selectedMachine && (
          <div className="detail-panel">
            <div className="panel-header">
              <h3>🔍 {selectedMachine.name}</h3>
              <button className="close-btn" onClick={() => { setShowDetails(false); setSelectedMachine(null); setCameraTarget(null); }}>✕</button>
            </div>
            <div className="panel-body">
              <div className="detail-group">
                <label>Type</label>
                <span className="detail-value">{selectedMachine.type}</span>
              </div>
              <div className="detail-group">
                <label>Status</label>
                <span style={{
                  display: 'inline-block', padding: '4px 8px',
                  background: statusColor + '20', border: `1px solid ${statusColor}`,
                  borderRadius: 4, color: statusColor, fontSize: 12,
                  fontWeight: 600, textTransform: 'uppercase',
                }}>
                  {selectedMachine.status}
                </span>
              </div>

              <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12 }}>
                <h4 style={{ color: '#e5e7eb', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
                  📊 Live Sensors
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selectedMachine.sensors?.length > 0 ? (
                    selectedMachine.sensors.map((sensor: any) => {
                      const val = sensorData[selectedMachine.machineId]?.[sensor.sensorId] ?? sensor.value;
                      const pct = ((val - sensor.min) / (sensor.max - sensor.min)) * 100;
                      const isWarn = pct > 80;
                      const isCrit = pct > 95;
                      return (
                        <div key={sensor.sensorId} style={{
                          padding: 10, background: 'rgba(59,130,246,0.1)',
                          borderRadius: 6, border: '1px solid rgba(59,130,246,0.3)',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#e5e7eb' }}>{sensor.name}</span>
                            <span style={{
                              fontSize: 12, fontWeight: 600, fontFamily: 'monospace',
                              color: isCrit ? '#f87171' : isWarn ? '#fbbf24' : '#34d399',
                            }}>
                              {val.toFixed(1)}{sensor.unit}
                            </span>
                          </div>
                          <div style={{ width: '100%', height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                              width: `${Math.min(pct, 100)}%`, height: '100%',
                              background: isCrit ? '#f87171' : isWarn ? '#fbbf24' : '#34d399',
                              transition: 'width 0.3s',
                            }} />
                          </div>
                          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                            Range: {sensor.min} – {sensor.max}{sensor.unit}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p style={{ color: '#9ca3af', fontSize: 12 }}>No sensors configured</p>
                  )}
                </div>
              </div>

              {activeTwin.anomalies?.length > 0 && (
                <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12 }}>
                  <h4 style={{ color: '#f87171', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>⚠️ Anomalies</h4>
                  {activeTwin.anomalies.map((a: string, i: number) => (
                    <div key={i} style={{
                      fontSize: 11, color: '#fca5a5', padding: 6, marginBottom: 4,
                      background: 'rgba(248,113,113,0.1)', borderRadius: 4,
                      borderLeft: '2px solid #f87171',
                    }}>{a}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Machine list overlay */}
        <div className="entity-overlay">
          <h3>⚙️ Machines</h3>
          <div className="hotspot-legend">
            <span className="hl-item"><span className="hl-dot" style={{background:'#34d399'}} />OK</span>
            <span className="hl-item"><span className="hl-dot" style={{background:'#fbbf24'}} />Warn</span>
            <span className="hl-item"><span className="hl-dot" style={{background:'#f87171'}} />Crit</span>
          </div>
          <div className="entity-list">
            {machines.map((m: any) => (
              <div
                key={m.machineId}
                className={`entity-item ${selectedMachine?.machineId === m.machineId ? 'selected' : ''}`}
                onClick={() => {
                  const idx = machines.indexOf(m);
                  const total = machines.length;
                  let pos: THREE.Vector3;
                  if (total <= 6) {
                    const angle = (idx / total) * Math.PI * 2 - Math.PI / 2;
                    const r = 5 + total * 0.5;
                    pos = new THREE.Vector3(Math.cos(angle) * r, 0.5, Math.sin(angle) * r);
                  } else {
                    const cols = Math.ceil(Math.sqrt(total));
                    const spacing = 6;
                    pos = new THREE.Vector3(
                      (idx % cols) * spacing - (cols * spacing) / 2 + spacing / 2,
                      0.5,
                      Math.floor(idx / cols) * spacing - spacing,
                    );
                  }
                  handleClick(m, pos);
                }}
              >
                <div className="entity-dot" style={{ background: STATUS_COLORS[m.status] || '#666' }} />
                <span className="entity-name">{m.name}</span>
                {(() => {
                  const h = computeMachineHealth(m, sensorData);
                  return h.alertCount > 0 ? (
                    <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: h.riskColor + '25', color: h.riskColor, fontWeight: 700 }}>
                      {h.alertCount}⚠
                    </span>
                  ) : null;
                })()}
                <span className={`badge badge-${m.status === 'running' ? 'active' : m.status === 'idle' ? 'warning' : 'error'}`}>
                  {m.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalTwinViewer;
